import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	type ExternalTestArtifactRun,
	type ExternalTestIsolationRule,
	type ExternalTestLayer,
	type ExternalTestLayerStatus,
	type ExternalTestReadinessCheck,
	type ExternalTestRunRequestDraftRequest,
	type ExternalTestsOverviewResponse,
	useExternalTestArtifactDetail,
	useExternalTestRunRequestDraft,
	useExternalTestsOverview,
} from "@/lib/api/external-tests";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/external-tests")({
	component: ExternalTestsPage,
});

function ExternalTestsPage() {
	const overview = useExternalTestsOverview();

	if (overview.isLoading) {
		return (
			<div className="mx-auto flex max-w-7xl flex-col gap-5">
				<PageHeader
					moduleId="EXT-T"
					title="外挂测试操作"
					description="读取 tau2 retail 外部测试状态、运行记录、证据包和人工测试申请草案。"
				/>
				<Card>
					<CardContent className="p-6 text-sm text-text-secondary">
						正在读取外挂测试状态...
					</CardContent>
				</Card>
			</div>
		);
	}

	if (overview.isError || !overview.data) {
		return (
			<div className="mx-auto flex max-w-7xl flex-col gap-5">
				<PageHeader
					moduleId="EXT-T"
					title="外挂测试操作"
					description="读取 tau2 retail 外部测试状态、运行记录、证据包和人工测试申请草案。"
				/>
				<Card>
					<CardContent className="p-6 text-sm text-brand-danger">
						外挂测试状态读取失败，请确认 Admin API 可用。
					</CardContent>
				</Card>
			</div>
		);
	}

	return <ExternalTestsDashboard data={overview.data} />;
}

function ExternalTestsDashboard({ data }: { data: ExternalTestsOverviewResponse }) {
	const validLayerCount = data.test_layers.filter(
		(layer) => layer.status === "artifact_valid",
	).length;
	const phaseSummary = data.can_start_external_tests
		? "已签署，可启动有限外部测试"
		: "等待签署确认";
	const decisionSummary =
		data.external_testing_decision === "adapter_only_r4_native_poc_allowed"
			? "Adapter / R4 / Native 预检已允许"
			: "等待外部测试决策";

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="EXT-T"
				title="外挂测试操作"
				description="面向 tau2 retail 的外部测试操作台：看状态、证据包、执行边界和人工申请草案，不替代内部评测运营。"
				help={externalTestsHelp}
			/>

			<section
				className="grid gap-3 md:grid-cols-2 xl:grid-cols-4"
				data-testid="external-tests-overview"
			>
				<MetricTile
					label="当前阶段"
					value={phaseSummary}
					detail={data.current_phase}
					tone="neutral"
				/>
				<MetricTile
					label="外部测试决策"
					value={decisionSummary}
					detail={data.external_testing_decision}
					tone="success"
				/>
				<MetricTile
					label="可读证据"
					value={`已归档可读 · ${validLayerCount}/${data.test_layers.length}`}
					tone="success"
				/>
				<MetricTile
					label="Admin 执行"
					value={data.admin_execution_enabled ? "可执行" : "只读展示"}
					tone="warning"
				/>
			</section>

			<section className="grid gap-5 xl:grid-cols-[minmax(360px,0.72fr)_minmax(0,1.28fr)]">
				<LayerPanel layers={data.test_layers} />
				<RunsPanel runs={data.latest_runs} layers={data.test_layers} />
			</section>

			<OperationGuidePanel data={data} />

			<RunFlowPanel data={data} />

			<RequestDraftPanel data={data} />
		</div>
	);
}

export function ExternalTestsGuideDashboard({ data }: { data: ExternalTestsOverviewResponse }) {
	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="EXT-T"
				title="外挂测试说明"
				description="解释 tau2 retail 外挂测试的能力范围、评价标准、任务集、隔离策略和回流边界。"
				help={externalTestsHelp}
			/>

			<section className="grid gap-5 xl:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)]">
				<CriteriaPanel data={data} />
				<TaskSetPanel data={data} />
			</section>

			<IsolationPanel rules={data.isolation_strategy} />

			<section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
				<BoundaryPanel />
				<FeedbackPanel data={data} />
			</section>
		</div>
	);
}

function MetricTile({
	label,
	value,
	detail,
	tone,
}: {
	label: string;
	value: string;
	detail?: string;
	tone: "success" | "warning" | "neutral";
}) {
	return (
		<div className="min-w-0 rounded-md border border-surface-border bg-surface-card p-4">
			<div className="text-xs font-medium text-text-muted">{label}</div>
			<div
				className={cn(
					"mt-2 break-words text-sm font-semibold leading-6",
					tone === "success"
						? "text-brand-success"
						: tone === "warning"
							? "text-brand-warning"
							: "text-text-primary",
				)}
			>
				{value}
			</div>
			{detail ? (
				<div className="mt-2 font-mono text-xs text-text-muted [overflow-wrap:anywhere]">
					{detail}
				</div>
			) : null}
		</div>
	);
}

function CriteriaPanel({ data }: { data: ExternalTestsOverviewResponse }) {
	return (
		<Card className="min-w-0" data-testid="external-tests-criteria">
			<CardHeader>
				<CardTitle className="text-lg">关键评价标准</CardTitle>
				<CardDescription>
					按 tau2 retail 能力信号解释结果，不把 reward 直接当内部质量门禁。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{data.evaluation_criteria.map((criterion) => (
					<div
						key={criterion.id}
						className="grid gap-3 rounded-md border border-surface-border bg-surface-background p-3 lg:grid-cols-[150px_minmax(0,1fr)]"
					>
						<div className="flex items-start gap-2">
							<CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand-success" aria-hidden />
							<div className="text-sm font-semibold text-text-primary">{criterion.label}</div>
						</div>
						<div className="space-y-2 text-sm leading-6 text-text-secondary">
							<p>{criterion.question}</p>
							<div className="grid gap-2 text-xs md:grid-cols-2">
								<FieldChip label="tau2 信号" value={criterion.tau2_signal} />
								<FieldChip label="本项目呈现" value={criterion.internal_presentation} />
							</div>
						</div>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function LayerPanel({ layers }: { layers: ExternalTestLayer[] }) {
	return (
		<Card className="min-w-0" data-testid="external-tests-layers">
			<CardHeader>
				<CardTitle className="text-lg">测试层级</CardTitle>
				<CardDescription>首版只展示证据状态，不从 Admin 触发 tau2 命令。</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{layers.map((layer) => (
					<div
						key={layer.test_layer}
						className="rounded-md border border-surface-border bg-surface-background p-3"
					>
						<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
							<div className="min-w-0">
								<div className="font-medium text-text-primary">{layer.label}</div>
								<div className="mt-1 text-xs text-text-muted">
									{layer.mode} · {layer.artifact_policy}
								</div>
							</div>
							<StatusPill status={layer.status} />
						</div>
						<div className="mt-3 grid gap-2 text-xs text-text-secondary md:grid-cols-2">
							<FieldChip label="required files" value={`${layer.required_files_count}`} />
							<FieldChip
								label="manual gate"
								value={layer.requires_manual_confirmation ? "需要人工确认" : "不在页面执行"}
							/>
						</div>
						<p className="mt-3 text-sm leading-6 text-text-secondary">{layer.next_action}</p>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function TaskSetPanel({ data }: { data: ExternalTestsOverviewResponse }) {
	return (
		<Card className="min-w-0" data-testid="external-tests-task-sets">
			<CardHeader>
				<CardTitle className="text-lg">retail 任务集</CardTitle>
				<CardDescription>
					第一轮只看 P0 支持探针和 P1 缺口探针，字段通过 adapter 映射。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{data.task_sets.map((taskSet) => (
					<div
						key={taskSet.id}
						className="rounded-md border border-surface-border bg-surface-background p-4"
					>
						<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
							<div>
								<div className="text-sm font-semibold text-text-primary">{taskSet.label}</div>
								<p className="mt-1 text-sm leading-6 text-text-secondary">{taskSet.description}</p>
							</div>
							<span className="w-fit rounded-md border border-surface-border bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary">
								{taskSet.task_count} 个任务
							</span>
						</div>
						<div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
							<FieldChip label="预期状态" value={taskSet.expected_statuses.join(" / ")} />
							<FieldChip label="测试模式" value={taskSet.test_modes.join(" / ")} />
						</div>
						<ul className="mt-3 space-y-2 text-sm leading-6 text-text-secondary">
							{taskSet.primary_questions_preview.map((question) => (
								<li key={question} className="flex gap-2">
									<span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary" />
									<span>{question}</span>
								</li>
							))}
						</ul>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function RunsPanel({
	runs,
	layers,
}: {
	runs: ExternalTestArtifactRun[];
	layers: ExternalTestLayer[];
}) {
	const layerLabelById = new Map(layers.map((layer) => [layer.test_layer, layer.label]));
	const [selectedRunId, setSelectedRunId] = useState<string | null>(runs[0]?.run_id ?? null);
	return (
		<Card className="min-w-0" data-testid="external-tests-runs">
			<CardHeader>
				<CardTitle className="text-lg">最近运行记录</CardTitle>
				<CardDescription>
					读取 artifact metadata。raw 结果和解释文档仍以 artifact 目录为事实源。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{runs.map((run) => (
					<div
						key={run.run_id}
						className="rounded-md border border-surface-border bg-surface-background p-3"
					>
						<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
							<div className="min-w-0">
								<div className="break-all font-mono text-xs font-semibold text-text-primary">
									{run.run_id}
								</div>
								<div className="mt-1 text-sm text-text-secondary">
									{layerLabelById.get(run.test_layer) ?? run.test_layer}
								</div>
							</div>
							<span className="w-fit rounded-md border border-brand-success/30 bg-brand-success/10 px-2 py-1 text-xs font-medium text-brand-success">
								returncode={run.command_returncode ?? "-"}
							</span>
						</div>
						<div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
							<FieldChip label="created" value={run.created_at} />
							<FieldChip label="artifact" value={run.artifact_dir} />
						</div>
						<div className="mt-3">
							<button
								type="button"
								className={cn(
									"rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
									selectedRunId === run.run_id
										? "border-brand-primary bg-brand-primary/10 text-brand-primary"
										: "border-surface-border bg-surface-card text-text-secondary hover:bg-surface-subtle",
								)}
								onClick={() => setSelectedRunId(run.run_id)}
							>
								查看证据包
							</button>
						</div>
					</div>
				))}
				<ArtifactDetailPanel runId={selectedRunId} />
			</CardContent>
		</Card>
	);
}

function ArtifactDetailPanel({ runId }: { runId: string | null }) {
	const detail = useExternalTestArtifactDetail(runId);

	if (runId === null) {
		return null;
	}

	if (detail.isLoading) {
		return (
			<div
				className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary"
				data-testid="external-tests-artifact-detail"
			>
				正在读取证据包...
			</div>
		);
	}

	if (detail.isError || !detail.data) {
		return (
			<div
				className="rounded-md border border-brand-danger/30 bg-brand-danger/10 p-3 text-sm text-brand-danger"
				data-testid="external-tests-artifact-detail"
			>
				证据包读取失败，请确认 artifact 是否已归档。
			</div>
		);
	}

	const data = detail.data;
	const visibleFiles = data.files.slice(0, 8);

	return (
		<section
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid="external-tests-artifact-detail"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
				<div className="min-w-0">
					<div className="text-sm font-semibold text-text-primary">证据包明细</div>
					<div className="mt-1 font-mono text-xs text-text-muted [overflow-wrap:anywhere]">
						{data.run.run_id}
					</div>
				</div>
				<span className="w-fit rounded-md border border-surface-border bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary">
					只读证据包 · {data.files.length} 个文件
				</span>
			</div>

			<div className="mt-4 grid min-w-0 gap-3 lg:grid-cols-2">
				<TextEvidenceBlock title="summary.zh-CN.md" text={data.summary_text} />
				<TextEvidenceBlock title="decision.zh-CN.md" text={data.decision_text} />
			</div>

			<div className="mt-4 space-y-3">
				<div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
					命令输出
				</div>
				{data.command_outputs.map((output) => (
					<div key={output.path} className="rounded-md bg-surface-subtle p-3">
						<div className="font-mono text-xs font-medium text-text-primary [overflow-wrap:anywhere]">
							{output.path}
						</div>
						<pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-card p-3 text-xs leading-5 text-text-secondary [overflow-wrap:anywhere]">
							{output.preview}
						</pre>
					</div>
				))}
			</div>

			<div className="mt-4 space-y-2">
				<div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
					文件清单
				</div>
				<div className="grid gap-2 md:grid-cols-2">
					{visibleFiles.map((file) => (
						<div key={file.path} className="rounded-md bg-surface-subtle px-2 py-1 text-xs">
							<span className="font-mono text-text-primary [overflow-wrap:anywhere]">
								{file.path}
							</span>
							<span className="ml-2 text-text-muted">
								{file.kind} · {file.size_bytes}B
							</span>
						</div>
					))}
				</div>
				{data.files.length > visibleFiles.length ? (
					<div className="text-xs text-text-muted">
						还有 {data.files.length - visibleFiles.length} 个文件未展开。
					</div>
				) : null}
			</div>
		</section>
	);
}

function TextEvidenceBlock({ title, text }: { title: string; text: string }) {
	return (
		<div className="min-w-0 rounded-md bg-surface-subtle p-3">
			<div className="text-xs font-semibold text-text-primary">{title}</div>
			<pre className="mt-2 max-h-36 overflow-auto whitespace-pre-wrap text-xs leading-5 text-text-secondary [overflow-wrap:anywhere]">
				{text || "暂无内容"}
			</pre>
		</div>
	);
}

function OperationGuidePanel({ data }: { data: ExternalTestsOverviewResponse }) {
	const offlineLayer = data.test_layers.find(
		(layer) => layer.test_layer === "offline_tau2_retail_poc",
	);
	const offlineStatus =
		offlineLayer?.status === "not_run" ? "离线零售 PoC 尚未运行" : "离线零售 PoC 已有记录";
	const executionStatus = data.admin_execution_enabled
		? "Admin 当前可执行受控测试"
		: "Admin 当前不执行测试";

	return (
		<Card className="min-w-0" data-testid="external-tests-operation-guide">
			<CardHeader>
				<CardTitle className="text-lg">操作与取数</CardTitle>
				<CardDescription>
					给产品经理看真实页面该怎么操作、去哪里拿数据，以及当前未纳入本阶段的自动执行环节。
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3 lg:grid-cols-4">
				<GuideTile
					title="真实页面"
					body="日常验收使用不带 mock 的线上页面。"
					code="https://app.bakabaka-workflows.shop/external-tests"
				/>
				<GuideTile
					title="真实接口"
					body="页面结果来自只读 Admin API，可直接打开查看 JSON。"
					code="/admin/v1/external-tests/overview"
				/>
				<GuideTile
					title="当前结果"
					body={`已有 ${data.latest_runs.length} 条运行记录，包含前置验证和已归档的离线 PoC。`}
					code={offlineStatus}
				/>
				<GuideTile
					title="下一步申请"
					body={`${executionStatus}。测试申请至少要说明范围、成本、运行层和禁止写操作。`}
					code="offline_tau2_retail_poc"
				/>
			</CardContent>
		</Card>
	);
}

function RunFlowPanel({ data }: { data: ExternalTestsOverviewResponse }) {
	const latestOfflineRun =
		data.latest_runs.find((run) => run.test_layer === "offline_tau2_retail_poc") ??
		data.latest_runs[0];
	const resultPointer = latestOfflineRun
		? `${latestOfflineRun.run_id} -> 查看证据包`
		: "等待 artifact 归档";
	const artifactPointer =
		latestOfflineRun?.artifact_dir ?? "artifacts/external-evaluation/tau2-bench/<run_id>/";

	return (
		<Card className="min-w-0" data-testid="external-tests-run-flow">
			<CardHeader>
				<CardTitle className="text-lg">草案后的运行闭环</CardTitle>
				<CardDescription>
					草案不是排队任务。人工确认后由 runner 在命令行执行，结果归档后回到本页查看。
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3 lg:grid-cols-4">
				<RunFlowStep
					step="1"
					title="生成草案"
					body="页面只生成交接摘要，不保存申请，不触发后台任务。"
					code="/admin/v1/external-tests/run-requests/draft"
				/>
				<RunFlowStep
					step="2"
					title="人工确认"
					body="确认成本、checkout、模型、任务范围、artifact 目录和禁止项。"
					code="manual_review_required"
				/>
				<RunFlowStep
					step="3"
					title="runner 执行"
					body="研发或评测负责人在服务器命令行显式执行。"
					code="uv run python tools/external-evaluation/tau2_adapter/run_isolated_native_poc.py --checkout /tmp/tau2-bench-review --execute --allow-llm-cost"
				/>
				<RunFlowStep
					step="4"
					title="回到本页看"
					body="artifact 归档后，本页最近运行记录会展示新 run。"
					code={resultPointer}
				/>
				<div className="lg:col-span-4">
					<FieldChip label="结果目录" value={artifactPointer} />
				</div>
			</CardContent>
		</Card>
	);
}

function RunFlowStep({
	step,
	title,
	body,
	code,
}: {
	step: string;
	title: string;
	body: string;
	code: string;
}) {
	return (
		<div className="min-w-0 rounded-md border border-surface-border bg-surface-background p-3">
			<div className="flex items-start gap-2">
				<span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-brand-primary/10 text-xs font-semibold text-brand-primary">
					{step}
				</span>
				<div className="min-w-0">
					<div className="text-sm font-semibold text-text-primary">{title}</div>
					<p className="mt-1 text-sm leading-6 text-text-secondary">{body}</p>
				</div>
			</div>
			<div className="mt-3 rounded-md bg-surface-subtle px-2 py-1 font-mono text-xs text-text-muted [overflow-wrap:anywhere]">
				{code}
			</div>
		</div>
	);
}

function IsolationPanel({ rules }: { rules: ExternalTestIsolationRule[] }) {
	return (
		<Card className="min-w-0" data-testid="external-tests-isolation">
			<CardHeader>
				<CardTitle className="text-lg">隔离策略</CardTitle>
				<CardDescription>
					真实效果测试可以继续推进，但执行、数据、工具、结果和回流都走旁路，不污染现有体系。
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3 lg:grid-cols-5">
				{rules.map((rule) => (
					<div
						key={rule.id}
						className="min-w-0 rounded-md border border-surface-border bg-surface-background p-3"
					>
						<div className="flex items-start gap-2">
							<ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand-primary" aria-hidden />
							<div className="min-w-0">
								<div className="text-sm font-semibold text-text-primary">{rule.label}</div>
								<p className="mt-1 text-sm leading-6 text-text-secondary">{rule.purpose}</p>
							</div>
						</div>
						<div className="mt-3 space-y-2 text-xs">
							<FieldChip label="保护面" value={rule.protected_surface} />
							<FieldChip label="允许路径" value={rule.allowed_path} />
						</div>
						<ul className="mt-3 space-y-1 text-xs leading-5 text-text-secondary">
							{rule.prohibited_actions.map((action) => (
								<li key={action} className="flex gap-2">
									<span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-danger" />
									<span>{action}</span>
								</li>
							))}
						</ul>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function RequestDraftPanel({ data }: { data: ExternalTestsOverviewResponse }) {
	const draft = useExternalTestRunRequestDraft();
	const [taskSetId, setTaskSetId] =
		useState<ExternalTestRunRequestDraftRequest["task_set_id"]>("p0_supported_probe");
	const [testLayer, setTestLayer] =
		useState<ExternalTestRunRequestDraftRequest["test_layer"]>("offline_tau2_retail_poc");
	const [objective, setObjective] = useState("验证 P0 支持探针是否可进入小样本 tau2 retail PoC。");
	const [allowLlmCost, setAllowLlmCost] = useState(true);

	const submitDraft = () => {
		draft.mutate({
			task_set_id: taskSetId,
			test_layer: testLayer,
			objective,
			allow_llm_cost: allowLlmCost,
			allow_project_agent_runtime: false,
			allow_shopify_live_write: false,
		});
	};

	return (
		<Card className="min-w-0" data-testid="external-tests-request-draft">
			<CardHeader>
				<CardTitle className="text-lg">测试申请草案</CardTitle>
				<CardDescription>
					生成给研发/评测负责人看的人工执行申请；这里只产出草案，不保存、不排队、不执行 tau2。
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
				<div className="space-y-3">
					<div className="block text-sm">
						<span className="font-medium text-text-primary">任务集</span>
						<Select
							className="mt-1 w-full"
							aria-label="任务集"
							value={taskSetId}
							onChange={(event) =>
								setTaskSetId(
									event.target.value as ExternalTestRunRequestDraftRequest["task_set_id"],
								)
							}
						>
							{data.task_sets.map((taskSet) => (
								<option key={taskSet.id} value={taskSet.id}>
									{taskSet.label} · {taskSet.id}
								</option>
							))}
						</Select>
					</div>
					<div className="block text-sm">
						<span className="font-medium text-text-primary">测试层</span>
						<Select
							className="mt-1 w-full"
							aria-label="测试层"
							value={testLayer}
							onChange={(event) =>
								setTestLayer(event.target.value as ExternalTestRunRequestDraftRequest["test_layer"])
							}
						>
							{data.test_layers.map((layer) => (
								<option key={layer.test_layer} value={layer.test_layer}>
									{layer.label} · {layer.test_layer}
								</option>
							))}
						</Select>
					</div>
					<label className="block text-sm">
						<span className="font-medium text-text-primary">测试目标</span>
						<textarea
							className="mt-1 min-h-20 w-full rounded-md border border-surface-border bg-surface-card px-3 py-2 text-sm leading-6 text-text-primary"
							value={objective}
							onChange={(event) => setObjective(event.target.value)}
						/>
					</label>
					<span className="flex items-center gap-2 text-sm text-text-secondary">
						<Switch checked={allowLlmCost} onCheckedChange={setAllowLlmCost} />
						已确认 LLM 成本预算
					</span>
					<div className="grid gap-2 text-xs text-text-muted md:grid-cols-2">
						<div className="rounded-md bg-surface-subtle px-2 py-1">
							项目真实 Agent runtime：关闭
						</div>
						<div className="rounded-md bg-surface-subtle px-2 py-1">Shopify live write：关闭</div>
					</div>
					<button
						type="button"
						className="rounded-md bg-brand-primary px-3 py-2 text-sm font-medium text-white hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
						onClick={submitDraft}
						disabled={draft.isPending || objective.trim().length === 0}
					>
						{draft.isPending ? "正在生成..." : "生成测试申请草案"}
					</button>
				</div>

				<div className="min-w-0 rounded-md border border-surface-border bg-surface-background p-3">
					<div className="text-sm font-semibold text-text-primary">草案结果</div>
					{draft.isError ? (
						<p className="mt-3 text-sm text-brand-danger">草案生成失败，请检查禁止项。</p>
					) : draft.data ? (
						<div className="mt-3 space-y-3 text-sm text-text-secondary">
							<FieldChip label="状态" value={draft.data.draft_status} />
							<FieldChip label="任务集" value={draft.data.task_set_id} />
							<FieldChip label="测试层" value={draft.data.test_layer} />
							<DraftList title="确认项" items={draft.data.required_confirmations} />
							<DraftCheckList items={draft.data.readiness_checks} />
							<DraftList title="禁止项" items={draft.data.blocked_actions} />
							<DraftList title="输出物" items={draft.data.expected_outputs} />
							<div>
								<div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
									交接摘要
								</div>
								<textarea
									className="mt-2 min-h-40 w-full resize-y rounded-md border border-surface-border bg-surface-card px-3 py-2 font-mono text-xs leading-5 text-text-secondary [overflow-wrap:anywhere]"
									readOnly
									value={draft.data.handoff_summary_markdown}
								/>
							</div>
						</div>
					) : (
						<p className="mt-3 text-sm leading-6 text-text-secondary">
							点击生成后，这里会出现人工审批状态、确认项、禁止项和预期输出物；后续仍由命令行 runner
							执行。
						</p>
					)}
				</div>
			</CardContent>
		</Card>
	);
}

function DraftCheckList({ items }: { items: ExternalTestReadinessCheck[] }) {
	return (
		<div>
			<div className="text-xs font-semibold uppercase tracking-wide text-text-muted">准备检查</div>
			<div className="mt-2 grid gap-2">
				{items.map((item) => (
					<div
						key={item.id}
						className="rounded-md border border-surface-border bg-surface-card px-3 py-2"
					>
						<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
							<div className="font-medium text-text-primary">{item.label}</div>
							<span
								className={cn(
									"w-fit rounded-md px-2 py-0.5 text-xs font-medium",
									item.status === "passed"
										? "bg-brand-success/10 text-brand-success"
										: item.status === "manual_required"
											? "bg-brand-warning/10 text-brand-warning"
											: "bg-brand-danger/10 text-brand-danger",
								)}
							>
								{readinessStatusLabel(item.status)}
							</span>
						</div>
						<p className="mt-1 text-sm leading-6 text-text-secondary">{item.detail}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function DraftList({ title, items }: { title: string; items: string[] }) {
	return (
		<div>
			<div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</div>
			<ul className="mt-2 space-y-1">
				{items.map((item) => (
					<li key={item} className="flex gap-2 text-sm leading-6">
						<span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-primary" />
						<span>{item}</span>
					</li>
				))}
			</ul>
		</div>
	);
}

function readinessStatusLabel(status: ExternalTestReadinessCheck["status"]) {
	if (status === "passed") {
		return "已满足";
	}
	if (status === "manual_required") {
		return "需人工确认";
	}
	return "已阻止";
}

function GuideTile({ title, body, code }: { title: string; body: string; code: string }) {
	return (
		<div className="min-w-0 rounded-md border border-surface-border bg-surface-background p-3">
			<div className="text-sm font-semibold text-text-primary">{title}</div>
			<p className="mt-2 text-sm leading-6 text-text-secondary">{body}</p>
			<div className="mt-3 rounded-md bg-surface-subtle px-2 py-1 font-mono text-xs text-text-muted [overflow-wrap:anywhere]">
				{code}
			</div>
		</div>
	);
}

function BoundaryPanel() {
	return (
		<Card className="min-w-0" data-testid="external-tests-boundaries">
			<CardHeader>
				<CardTitle className="text-lg">边界</CardTitle>
				<CardDescription>外挂测试只做外部证据视图，内部评测功能继续保留。</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3 md:grid-cols-2">
				<BoundaryItem
					icon="shield"
					title="只读 Admin 入口"
					body="不从页面执行 tau2 run，不接真实 Agent runtime。"
				/>
				<BoundaryItem
					icon="alert"
					title="不执行 Shopify live write"
					body="取消、退货、改地址等只看 dry-run 或 sandbox 证据。"
				/>
				<BoundaryItem
					icon="file"
					title="不自动写回 EvalRun"
					body="不自动创建 Badcase，不自动写回 Regression。"
				/>
				<BoundaryItem
					icon="shield"
					title="不做字段级强对齐"
					body="通过 adapter 和概念映射衔接 tau2 retail 任务。"
				/>
			</CardContent>
		</Card>
	);
}

function FeedbackPanel({ data }: { data: ExternalTestsOverviewResponse }) {
	return (
		<Card className="min-w-0">
			<CardHeader>
				<CardTitle className="text-lg">回流路线</CardTitle>
				<CardDescription>结果先进入人工判断，再决定是否转能力补齐或 adapter 修正。</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{data.feedback_routes.map((route) => (
					<div key={route.route} className="rounded-md bg-surface-subtle p-3 text-sm">
						<div className="font-medium text-text-primary">{route.route}</div>
						<div className="mt-1 text-text-secondary">{route.condition}</div>
						<div className="mt-2 font-mono text-xs text-text-muted [overflow-wrap:anywhere]">
							{route.target}
						</div>
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function FieldChip({ label, value }: { label: string; value: string }) {
	return (
		<div className="min-w-0 rounded-md bg-surface-subtle px-2 py-1">
			<span className="text-text-muted">{label}: </span>
			<span className="text-text-secondary [overflow-wrap:anywhere]">{value}</span>
		</div>
	);
}

function StatusPill({ status }: { status: ExternalTestLayerStatus }) {
	const copy = {
		artifact_valid: "证据完整",
		artifact_incomplete: "待补 artifact",
		artifact_failed: "校验失败",
		not_run: "未运行",
	}[status];
	const tone =
		status === "artifact_valid"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: status === "not_run"
				? "border-surface-border bg-surface-subtle text-text-secondary"
				: "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";
	return (
		<span className={cn("inline-flex w-fit rounded-md border px-2 py-1 text-xs font-medium", tone)}>
			{copy}
		</span>
	);
}

function BoundaryItem({
	icon,
	title,
	body,
}: {
	icon: "shield" | "alert" | "file";
	title: string;
	body: string;
}) {
	const Icon = icon === "shield" ? ShieldCheck : icon === "alert" ? AlertTriangle : FileText;
	return (
		<div className="flex gap-3 rounded-md border border-surface-border bg-surface-background p-3">
			<Icon className="mt-0.5 h-4 w-4 flex-none text-brand-primary" aria-hidden />
			<div>
				<div className="text-sm font-semibold text-text-primary">{title}</div>
				<p className="mt-1 text-sm leading-6 text-text-secondary">{body}</p>
			</div>
		</div>
	);
}

const externalTestsHelp: PageHelp = {
	title: "外挂测试说明",
	description: "这里展示 tau2 retail 外部测试的状态和证据，不替代内部评测运营。",
	sections: [
		{
			title: "这里看什么",
			items: [
				"当前是否进入签署后的外部测试准备态。",
				"tau2 retail 首轮任务集和五条评价标准。",
				"四层测试链路的 artifact 状态和运行记录。",
			],
		},
		{
			title: "不要误读",
			items: [
				"页面不执行 tau2 run，也不触发真实 Agent runtime。",
				"外部结果不会自动写成 EvalRun、Badcase 或回归样本。",
				"新一轮离线零售 PoC 仍需要人工确认模型、成本和运行边界。",
			],
		},
	],
};
